import React, { useState, useEffect } from 'react';
import AdminPanel from './adminPanel.js';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  IconButton,
  Collapse
} from "@mui/material";
import {
  Logout as LogoutIcon,
  Devices as DevicesIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const ensureHttps = (url) => {
  if (url.startsWith('/')) return url;
  return url.replace(/^http:/, 'https:');
};

const secureFetch = async (url, options = {}) => {
  const secureUrl = ensureHttps(url);
  return fetch(secureUrl, {
    ...options,
    credentials: 'same-origin',
  });
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDevices();
    const interval = setInterval(() => {
      loadDevices();
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user && currentView === 'admin' && (user.IsAdmin || user.IsSuperAdmin)) {
      loadUsers();
    }
  }, [currentView, user]);

  const checkAuth = async () => {
    try {
      const response = await secureFetch('/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
    setLoading(false);
  };

  const loadDevices = async () => {
    try {
      const response = await secureFetch('/proxy/devices');
      if (response.ok) {
        const devicesData = await response.json();
        setDevices(devicesData);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await secureFetch('/admin/users');
      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const logout = async () => {
    await secureFetch('/auth/logout', { method: 'POST' });
    setUser(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {user && (
        <AppBar position="sticky">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              PufferConsole
            </Typography>
            <Button
              color="inherit"
              startIcon={<DevicesIcon />}
              onClick={() => setCurrentView('dashboard')}
            >
              My Devices
            </Button>
            {(user.IsAdmin || user.IsSuperAdmin) && (
              <Button
                color="inherit"
                startIcon={<AdminIcon />}
                onClick={() => setCurrentView('admin')}
              >
                Admin
              </Button>
            )}
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {user.Email}
              </Typography>
              {user.IsSuperAdmin && <Chip label="Super Admin" size="small" color="secondary" />}
              {user.IsAdmin && !user.IsSuperAdmin && <Chip label="Admin" size="small" color="primary" />}
              <IconButton color="inherit" onClick={logout}>
                <LogoutIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth={false} sx={{ py: 4 }}>
        {!user ? (
          <LoginForm onLogin={setUser} />
        ) : currentView === 'admin' && (user.IsAdmin || user.IsSuperAdmin) ? (
          <AdminPanel
            users={users}
            devices={devices}
            onRefresh={() => { loadDevices(); loadUsers(); }}
          />
        ) : (
          <Dashboard devices={devices} onRefresh={loadDevices} />
        )}
      </Container>
    </Box>
  );
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const endpoint = isRegister ? '/auth/register' : '/auth/login';

    try {
      const response = await secureFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        if (isRegister) {
          setIsRegister(false);
          setError('Account created! Please login.');
        } else {
          onLogin(data.user);
        }
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" gutterBottom align="center">
          {isRegister ? 'Register' : 'Login'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3 }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
          >
            {isRegister ? 'Register' : 'Login'}
          </Button>

          {error && (
            <Typography color="error" sx={{ mt: 2 }} align="center">
              {error}
            </Typography>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}
            </Typography>
            <Button onClick={() => setIsRegister(!isRegister)}>
              {isRegister ? 'Login' : 'Register'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function Dashboard({ devices, onRefresh }) {
  const [expandedDeviceId, setExpandedDeviceId] = React.useState(null);
  const [deviceDetails, setDeviceDetails] = React.useState({});

  const openDevice = (deviceId) => {
    const url = ensureHttps(`/proxy/${deviceId}/`);

    if (navigator.userAgent.includes("PufferConsoleRemoteMaui")) {
      // Inside MAUI WebView -> open in same view
      window.location.href = url;
    } else {
      // Normal browser -> open new tab
      window.open(url, "_blank");
    }
  };

  const loadDeviceDetails = async (deviceId) => {
    setDeviceDetails((prev) => ({
      ...prev,
      [deviceId]: {
        ...(prev[deviceId] || {}),
        loading: true,
        error: null
      }
    }));

    try {
      const [hardwareRes, healthRes] = await Promise.all([
        secureFetch(`/proxy/devices/${encodeURIComponent(deviceId)}/hardware-latest`),
        secureFetch(`/proxy/devices/${encodeURIComponent(deviceId)}/health-latest`)
      ]);

      const hardware = hardwareRes.ok ? await hardwareRes.json() : null;
      const health = healthRes.ok ? await healthRes.json() : null;

      setDeviceDetails((prev) => ({
        ...prev,
        [deviceId]: {
          hardware,
          health,
          loading: false,
          error: null
        }
      }));
    } catch (error) {
      console.error("Failed to load device details:", error);
      setDeviceDetails((prev) => ({
        ...prev,
        [deviceId]: {
          ...(prev[deviceId] || {}),
          loading: false,
          error: "Failed to load device details."
        }
      }));
    }
  };


  const toggleExpanded = (device) => {
    const id = device.DeviceId;
    if (expandedDeviceId === id) {
      setExpandedDeviceId(null);
      return;
    }

    setExpandedDeviceId(id);

    if (!deviceDetails[id] || (!deviceDetails[id].hardware && !deviceDetails[id].health)) {
      loadDeviceDetails(id);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography variant="h4">Your Devices</Typography>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh} variant="outlined">
          Refresh
        </Button>
      </Box>

      {devices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No devices paired yet.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {devices.map((device) => {
            const id = device.DeviceId;
            const details = deviceDetails[id] || {};
            const isExpanded = expandedDeviceId === id;

            return (
              <Card key={device.Id} sx={{ width: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mr: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {device.Name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Last seen: {new Date(device.LastSeen).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                      <Chip
                        label={device.IsOnline ? "Online" : "Offline"}
                        color={device.IsOnline ? "success" : "default"}
                        size="small"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleExpanded(device)}
                      >
                        {isExpanded ? "Hide details" : "Show details"}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                  <Button
                    size="small"
                    onClick={() => openDevice(device.DeviceId)}
                    disabled={!device.IsOnline}
                  >
                    Open Console
                  </Button>
                </CardActions>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                    {details.loading && (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}

                    {details.error && (
                      <Typography color="error" variant="body2">
                        {details.error}
                      </Typography>
                    )}

                    {!details.loading && !details.error && (details.hardware || details.health) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: 3,
                          mt: 1
                        }}
                      >
                        {details.hardware && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Hardware Profile
                            </Typography>
                            <Typography variant="body2">
                              Machine: {details.hardware.MachineName}
                            </Typography>
                            <Typography variant="body2">
                              OS: {details.hardware.OsVersion}
                            </Typography>
                            <Typography variant="body2">
                              CPU: {details.hardware.CpuName} (
                              {details.hardware.LogicalCpuCount} logical /{" "}
                              {details.hardware.PhysicalCpuCount} physical)
                            </Typography>
                            <Typography variant="body2">
                              RAM:{" "}
                              {Math.round(
                                (details.hardware.TotalMemoryBytes || 0) / (1024 * 1024 * 1024)
                              )}
                              {" GB"}
                            </Typography>
                            {details.hardware.GpuName && (
                              <Typography variant="body2">
                                GPU: {details.hardware.GpuName}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {details.health && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Health (latest)
                            </Typography>
                            <Typography variant="body2">
                              Sampled:{" "}
                              {new Date(details.health.TimestampUtc).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              CPU Usage: {details.health.CpuUsagePercent?.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2">
                              Memory Usage: {details.health.MemoryUsagePercent?.toFixed(1)}%
                            </Typography>
                            {details.health.SystemDiskUsagePercent != null && (
                              <Typography variant="body2">
                                System Disk: {details.health.SystemDiskUsagePercent.toFixed(1)}%
                              </Typography>
                            )}
                            {details.health.CpuTemperatureC != null && (
                              <Typography variant="body2">
                                CPU Temp: {details.health.CpuTemperatureC.toFixed(1)} °C
                              </Typography>
                            )}
                            {details.health.GpuTemperatureC != null && (
                              <Typography variant="body2">
                                GPU Temp: {details.health.GpuTemperatureC.toFixed(1)} °C
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {!details.loading && !details.error && !details.hardware && !details.health && (
                      <Typography variant="body2" color="text.secondary">
                        No health or hardware data reported yet.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default App;